import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Truck, AlertTriangle } from "lucide-react";
import api from "./axiosConfig";
import { useLanguage } from "./LanguageContext";

export default function MerchantOrderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      const res = await api.get(`/orders/${id}/merchant`);
      setOrder(res.data.order);
    } catch (err) {
      console.error("Erreur chargement commande", err);
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F7FA]">
        {t('loading_dots')}
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F7FA]">
        {t('order_not_found')}
      </div>
    );
  }

  const paymentLabel =
    order.paymentMethod === "cash"
      ? t('pay_cash')
      : order.paymentMethod === "mobile"
      ? t('pay_mobile')
      : order.paymentMethod === "card"
      ? t('pay_card')
      : "—";

  const deliveryLabel =
    order.fulfillmentType === "click_collect"
      ? t('full_collect')
      : order.fulfillmentType === "delivery"
      ? t('full_delivery')
      : "—";

  return (
    <div className="min-h-screen bg-[#F5F7FA] p-8 font-sans text-slate-800">
      {/* HEADER */}
      <div className="flex items-center justify-between max-w-4xl mx-auto mb-6">
        <div>
          <h1 className="text-2xl font-extrabold">{t('order_details')}</h1>
          <p className="text-sm text-gray-500">{t('order_unit')} #{order.orderNumber}</p>
        </div>

        <button
          onClick={() => navigate("/merchant/orders")}
          className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-[#FF3B30] rounded-xl"
        >
          <ArrowLeft size={16} />
          {t('back')}
        </button>
      </div>

      {/* CONTENU */}
      <div className="max-w-4xl p-6 mx-auto space-y-6 bg-white border shadow rounded-2xl">
        {/* BANNIÈRE DE SURVEILLANCE LIVRAISON */}
        {order.fulfillmentType === "delivery" && (
          <div className="flex items-center justify-between p-5 mb-2 border-2 border-blue-500 bg-blue-50 rounded-2xl animate-pulse">
            <div className="flex items-center gap-4">
              <div className="p-3 text-white bg-blue-600 rounded-full shadow-lg shadow-blue-200">
                <Truck size={28} />
              </div>
              <div>
                <h3 className="text-xl font-black text-blue-900 uppercase leading-none">Commande à Livrer</h3>
                <p className="mt-1 text-sm font-bold text-blue-600 italic">Préparez le colis pour le livreur immédiatement</p>
              </div>
            </div>
            <div className="hidden md:block">
               <AlertTriangle size={32} className="text-blue-400 opacity-50" />
            </div>
          </div>
        )}

        {/* INFOS CLIENT & PAIEMENT/RÉCEPTION */}
        <div className="grid gap-6 md:grid-cols-2">
            <div>
                <h2 className="mb-2 text-sm font-bold text-gray-500 uppercase">{t('client_info')}</h2>
                <div className="space-y-1 text-sm">
                    <p><span className="font-semibold">{t('phone')} :</span> {order.customerPhone || "—"}</p>
                    <p><span className="font-semibold">{t('address')} :</span> {order.address || "—"}</p>
                </div>
            </div>

            <div>
                <h2 className="mb-2 text-sm font-bold text-gray-500 uppercase">{t('payment_reception')}</h2>
                <div className="flex flex-col gap-2">
                    <p className="text-sm"><strong>{t('payment')} :</strong> {paymentLabel}</p>
                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg w-fit ${
                        order.fulfillmentType === "delivery" 
                        ? "bg-blue-600 text-white font-black" 
                        : "bg-gray-100 text-gray-700 font-bold"
                    }`}>
                        {order.fulfillmentType === "delivery" && <Truck size={14} />}
                        {deliveryLabel}
                    </div>
                </div>
            </div>
        </div>

        {/* NOTE CLIENT */}
        {order.note && order.note.trim() !== "" && (
          <div>
            <h2 className="mb-2 text-sm font-bold text-gray-500 uppercase">
              {t('client_note')}
            </h2>
            <div className="p-4 text-sm text-gray-700 whitespace-pre-line border bg-gray-50 rounded-xl">
              {order.note}
            </div>
          </div>
        )}

        {/* PREUVE DE PAIEMENT */}
        {order.paymentProof && (
        <div>
            <h2 className="mb-2 text-sm font-bold text-gray-500 uppercase">
            {t('payment_proof')}
            </h2>

            <img
            src={order.paymentProof.startsWith('http') 
                ? order.paymentProof 
                : `${process.env.REACT_APP_API_URL}${order.paymentProof}`}
            alt="Preuve"
            className="max-w-md border rounded-xl"
            />
        </div>
        )}

        {/* PRODUITS */}
        <div>
          <h2 className="mb-4 text-sm font-bold text-gray-500 uppercase tracking-wider">
            {t('items_ordered')}
          </h2>
        
          <div className="space-y-4">
            {order.items.map((item, index) => (
              <div key={index} className="flex items-center gap-6 py-4 border-b last:border-b-0 border-gray-100">
                
                {/* 🖼️ IMAGE AGRANDIE ET CLAIRE */}
                <div className="w-24 h-24 flex-shrink-0 bg-gray-50 rounded-2xl overflow-hidden border border-gray-200">
                  {item.image ? (
                    <img 
                      src={item.image.startsWith('http') ? item.image : `${process.env.REACT_APP_API_URL}${item.image}`}
                      alt={item.name}
                      className="w-full h-full object-contain p-1" // object-contain pour voir tout le produit
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[10px] text-gray-400 text-center p-1">
                      {t('no_img') || "No image"}
                    </div>
                  )}
                </div>
        
                {/* INFOS PRODUIT */}
                <div className="flex-1 min-w-0">
                  <div className="text-base font-extrabold text-slate-800">
                    {item.name}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    <span className="bg-gray-100 px-2 py-0.5 rounded-md font-bold text-slate-700">
                        {t('quantity') || 'Qté'}: {item.quantity}
                    </span>
                  </div>
                </div>
        
                {/* PRIX */}
                <div className="text-base font-black text-slate-700 whitespace-nowrap">
                  {item.price * item.quantity} MRU
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* TOTAL */}
        <div className="pt-4 border-t">
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex justify-between">
              <span>{t('subtotal')}</span>
              <span className="font-medium">{order.subtotal ?? 0} MRU</span>
            </div>

            <div className="flex justify-between">
              <span>{t('service_fee')}</span>
              <span className="font-medium">{order.serviceFee ?? 0} MRU</span>
            </div>

            <div className="flex justify-between text-gray-400">
              <span>{t('delivery_fee')}</span>
              <span className="font-medium">{order.deliveryFee ?? 0} MRU</span>
            </div>
        </div>

        <div className="flex justify-between pt-4 mt-4 text-lg font-bold border-t border-dashed">
          <span>{t('total_paid')}</span>
          <span className="text-[#FF3B30]">
            {(order.totalAmount ?? order.total ?? 0)} MRU
          </span>
        </div>
        </div>
    </div>
    </div>
  );
}
